import type {
  ProductFinderErrorStage,
  ProductFinderType,
  ProductFinderViewType
} from './types';

type LensParameterType =
  | 'workingDistanceAndObjectHeight'
  | 'workingDistanceAndObjectWidth'
  | 'focalLength'
  | 'viewAngle';

export function getProductFinderItemListMeta(finder: ProductFinderType): {
  item_list_id: string;
  item_list_name: string;
} {
  if (finder === 'framegrabber') {
    return {
      item_list_id: 'framegrabber_finder',
      item_list_name: 'Framegrabber Finder'
    };
  }

  return {
    item_list_id: 'lens_finder',
    item_list_name: 'Lens Finder'
  };
}

export function createFinderSessionId(
  entry: 'pdp' | 'cart',
  finder: ProductFinderType
): string {
  const prefix = entry === 'cart' ? `pf_cart_${finder}_` : `pf_${finder}_`;
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `${prefix}${crypto.randomUUID()}`;
  }

  return `${prefix}${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function mapLensParameterToCalculationMethod(
  parameterType: LensParameterType | null
): string | undefined {
  switch (parameterType) {
    case 'workingDistanceAndObjectHeight':
      return 'working_distance_object_height';
    case 'workingDistanceAndObjectWidth':
      return 'working_distance_object_width';
    case 'focalLength':
      return 'focal_length';
    case 'viewAngle':
      return 'field_of_view';
    default:
      return undefined;
  }
}

export function mapLensBadgeTypeToResultBadge(
  badgeType: 'bestTechnicalFit' | 'bestPricePerformance' | null
): 'best_technical_fit' | 'best_price_performance' | 'none' {
  if (badgeType === 'bestTechnicalFit') {
    return 'best_technical_fit';
  }
  if (badgeType === 'bestPricePerformance') {
    return 'best_price_performance';
  }

  return 'none';
}

export function mapFramegrabberTierToResultBadge(
  tier: 'Premium' | 'Standard' | null
): 'premium' | 'none' {
  if (tier === 'Premium') {
    return 'premium';
  }

  return 'none';
}

export function mapProductListViewToAnalyticsViewType(
  view: 'table-list' | 'grid'
): ProductFinderViewType {
  if (view === 'grid') {
    return 'tile';
  }

  return 'list';
}

export function buildLensInputValues(params: {
  workingDistance?: string;
  objectHeight?: string;
  objectWidth?: string;
  focalLength?: string;
  viewAngleHorizontal?: string;
  viewAngleVertical?: string;
}): string {
  const parts: string[] = [];

  if (params.workingDistance) {
    parts.push(`working_distance=${params.workingDistance}`);
  }
  if (params.objectHeight) {
    parts.push(`object_height=${params.objectHeight}`);
  }
  if (params.objectWidth) {
    parts.push(`object_width=${params.objectWidth}`);
  }
  if (params.focalLength) {
    parts.push(`focal_length=${params.focalLength}`);
  }
  if (params.viewAngleHorizontal) {
    parts.push(`view_angle_horizontal=${params.viewAngleHorizontal}`);
  }
  if (params.viewAngleVertical) {
    parts.push(`view_angle_vertical=${params.viewAngleVertical}`);
  }

  return parts.join(';');
}

export function mapProductFinderErrorKeyToStage(
  errorKey: string
): ProductFinderErrorStage {
  if (errorKey.includes('cameraDataLoad')) {
    return 'camera_data_load';
  }
  if (
    errorKey.includes('recommendationCalculationFailed') ||
    errorKey.includes('invalidFocalLength')
  ) {
    return 'calculation';
  }
  if (
    errorKey.includes('noLensesAvailable') ||
    errorKey.includes('framegrabberLoad') ||
    errorKey.includes('noFramegrabberAvailable') ||
    errorKey.includes('framegrabberMissingAttributes')
  ) {
    return 'recommendation_load';
  }
  if (errorKey.includes('noCompatibleProducts')) {
    return 'tool_init';
  }

  return 'recommendation_load';
}

export function mapProductFinderErrorKeyToCode(errorKey: string): string {
  if (errorKey.includes('cameraDataLoad')) {
    return 'camera_data_unavailable';
  }
  if (errorKey.includes('invalidFocalLength')) {
    return 'invalid_focal_length';
  }
  if (errorKey.includes('recommendationCalculationFailed')) {
    return 'calculation_failed';
  }
  if (errorKey.includes('noLensesAvailable')) {
    return 'no_lenses_available';
  }
  if (errorKey.includes('noFramegrabberAvailable')) {
    return 'no_framegrabber_available';
  }
  if (errorKey.includes('framegrabberMissingAttributes')) {
    return 'framegrabber_missing_attributes';
  }
  if (errorKey.includes('framegrabberLoad')) {
    return 'framegrabber_load_failed';
  }
  if (errorKey.includes('noCompatibleProducts')) {
    return 'no_compatible_products';
  }

  return 'unknown_error';
}
